"use client";
import { IconMagnifyingGlass } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconChevronDownOutline18 } from "nucleo-ui-outline-18";

import { Input } from "@/components/ui/input";
import { SPECIAL_CATEGORY_TRIGGERS, SPECIAL_CATEGORY_X402, useApps } from "@/hooks/useApps";
import { useMemo, useState } from "react";
import { PublicAppCard } from "../../../_components/PublicAppCard";

const INITIAL_VISIBLE = 12;
const LOAD_MORE_STEP = 12;

interface IntegrationDetailComboGridProps {
  currentSlug: string;
  appName: string;
}

export function IntegrationDetailComboGrid({ currentSlug, appName }: IntegrationDetailComboGridProps) {
  const { apps, isLoading } = useApps();
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  const filteredApps = useMemo(() => {
    const specialSlugs = [SPECIAL_CATEGORY_TRIGGERS, SPECIAL_CATEGORY_X402];
    let filtered = apps.filter(a => a.slug !== currentSlug && !specialSlugs.includes(a.slug) && !a.isSpecialCategory);

    if (searchQuery) {
      const term = searchQuery.toLowerCase();
      filtered = filtered.filter(a => a.name.toLowerCase().includes(term) || a.slug.toLowerCase().includes(term));
    }

    return filtered;
  }, [apps, currentSlug, searchQuery]);

  const visibleApps = filteredApps.slice(0, visibleCount);
  const remaining = filteredApps.length - visibleCount;

  return (
    <section className="flex flex-col gap-5">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <h2 className="text-ds-text-primary leading-label text-2xl font-medium">
          Create workflows with <span className="font-bold">{appName}</span> integrations
        </h2>

        <div className="rounded-ds-md bg-ds-surface-sunken border-ds-border shadow-ds-edge-light relative flex w-full items-center gap-2 border py-2.5 pl-2.5 pr-2 sm:w-[260px]">
          <IconMagnifyingGlass className="text-ds-text-disabled h-5 w-5 shrink-0" />
          <Input
            type="text"
            placeholder="Search integrations..."
            value={searchQuery}
            onChange={e => {
              setSearchQuery(e.target.value);
              setVisibleCount(INITIAL_VISIBLE);
            }}
            className="text-ds-text-primary placeholder:text-ds-text-disabled h-auto flex-1 border-0 bg-transparent p-0 text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
          />
          <span className="rounded-inherit shadow-ds-debossed pointer-events-none absolute inset-[-0.5px]" />
        </div>
      </div>
      {isLoading ? (
        <div className="text-ds-text-muted flex h-40 items-center justify-center">Loading integrations...</div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-[repeat(auto-fill,minmax(170px,1fr))] sm:gap-5 lg:grid-cols-4">
          {visibleApps.map(app => {
            const [a, b] = [currentSlug, app.slug].sort();
            return <PublicAppCard key={app.slug} app={app} href={`/integrations/combo/${a}-and-${b}`} />;
          })}
        </div>
      )}
      {remaining > 0 && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => setVisibleCount(c => c + LOAD_MORE_STEP)}
            className="text-ds-focus-brand flex items-center gap-1 rounded-lg px-5 py-2 text-sm font-medium transition-colors hover:bg-black/5"
          >
            Load more
            <IconChevronDownOutline18 className="h-4 w-4" />
          </button>
        </div>
      )}
    </section>
  );
}
